import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { WorkbookOrderField, WorkbooksDisplayGridRowDataFragment } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  TableauUser,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import { WorkbooksValidSortFields } from '../sorting/sortProviders/WorkbooksGridSortProvider';

const shouldBeObfuscated: (rowData: WorkbooksDisplayGridRowData) => boolean = ({ name }) => name === null;
const projectContainerType = 'project';

export interface WorkbooksDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly vizportalUrlId: string;
  readonly owner: TableauUser;
  readonly sheetCount: number | null;
  readonly containerName: string | null;
  readonly containerType: string;
  readonly projectVizportalUrlId: string | null;
}

export function getRowDataForWorkbook(workbook: WorkbooksDisplayGridRowDataFragment): WorkbooksDisplayGridRowData {
  return {
    id: workbook.id,
    name: workbook.name,
    vizportalUrlId: workbook.vizportalUrlId,
    owner: workbook.owner,
    sheetCount: workbook.sheetsConnection ? workbook.sheetsConnection.totalCount : null,
    containerName: workbook.containerName,
    containerType: workbook.containerType,
    projectVizportalUrlId: workbook.projectVizportalUrlId,
  };
}

type WorkbooksColumnFactory = ColumnFactory<GridSortProps<WorkbooksValidSortFields>, WorkbooksDisplayGridRowData, WorkbooksValidSortFields>;

function columns(): ReadonlyArray<WorkbooksColumnFactory> {
  const cols: ReadonlyArray<WorkbooksColumnFactory> = [
    (sortParams) => ({
      name: 'name',
      dataKey: WorkbookOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => urls.workbookLineagePath(vizportalUrlId)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'sheets',
      dataKey: WorkbookOrderField.SHEETS_COUNT,
      headerText: Messages.GridHeaderSheets(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter<WorkbooksDisplayGridRowData>(({ sheetCount }) => sheetCount),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'location',
      dataKey: WorkbookOrderField.PROJECT_NAME,
      headerText: Messages.GridHeaderLocation(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ containerType, containerName }) => {
          return containerType === projectContainerType ? containerName : Messages.PersonalSpaceName();
        },
        (urls, { projectVizportalUrlId, containerType }) => (projectVizportalUrlId && containerType === projectContainerType ? urls.projectPath(projectVizportalUrlId) : null)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(({ owner }) => owner),
      resizable: true,
      sortable: false,
    }),
  ];

  return cols.filter(Boolean) as ReadonlyArray<WorkbooksColumnFactory>;
}

export type WorkbooksDisplayGridProps = UsualDisplayGridProps<WorkbooksDisplayGridRowData, GraphQLError> &
  GridSortProps<WorkbooksValidSortFields>;

export function WorkbooksDisplayGrid(props: WorkbooksDisplayGridProps): JSX.Element {
  const cols = columns().map((columnFactory) => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
